import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button.jsx';
import {
  Copy, CheckCircle2, Globe, MapPin, ShieldCheck,
  Upload, Clock,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import OrnateDivider from '../components/OrnateDivider';
import { authUrlWithNext } from '../lib/whatsapp';
import { loadRazorpayCheckout, RAZORPAY_LOAD_ERROR } from '../lib/razorpay';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const RAZORPAY_KEY_ID = process.env.REACT_APP_RAZORPAY_KEY_ID;
const UPI_ID = 'bhandari.sanjay-1@okicici';
const UPI_QR = `https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(
  'upi://pay?pa=' + UPI_ID + '&pn=Siddha%20Samaj&am=3000&cu=INR'
)}`;

const loadRazorpayScript = loadRazorpayCheckout;

const CopyButton = ({ value, label = 'Copy', testid }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      toast.success('Copied to clipboard');
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error('Copy failed');
    }
  };
  return (
    <button
      type="button"
      onClick={handleCopy}
      className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-md border border-primary/40 text-primary hover:bg-primary/10 transition"
      data-testid={testid}
    >
      {copied ? <CheckCircle2 size={12} /> : <Copy size={12} />}
      <span>{copied ? 'Copied' : label}</span>
    </button>
  );
};

const TabButton = ({ active, onClick, icon: Icon, children, testid }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 text-sm font-heading uppercase tracking-[0.25em] transition-all ${
      active
        ? 'text-primary border-b-2 border-primary bg-primary/5'
        : 'text-primary/50 border-b-2 border-transparent hover:text-primary/80'
    }`}
  >
    <Icon size={16} /> {children}
  </button>
);

const EnrollLevel1 = () => {
  const { user, loading: authLoading, refreshUser, token } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('india');
  const [processing, setProcessing] = useState(false);
  const [declareMethod, setDeclareMethod] = useState('upi');
  const [txnRef, setTxnRef] = useState('');
  const [screenshotUrl, setScreenshotUrl] = useState('');
  const [declareNotes, setDeclareNotes] = useState('');
  const [declaring, setDeclaring] = useState(false);
  const [myPending, setMyPending] = useState(null); // { id, status, txn_ref }
  const alreadyEnrolled = !!user?.enrolled_levels?.includes(1);

  useEffect(() => {
    if (!authLoading && !user) {
      toast.message('Please sign in to enroll');
      navigate(authUrlWithNext({ pathname: '/enroll/level-1' }), { replace: true });
    }
  }, [user, authLoading, navigate]);

  // Fetch the user's most recent pending declaration (if any)
  useEffect(() => {
    const run = async () => {
      if (!token) return;
      try {
        const r = await axios.get(`${API}/enrollments/my`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const pending = (r.data || []).find(
          (e) => e.payment_status === 'pending_review' || e.status === 'pending_review'
        );
        if (pending) {
          setMyPending({
            id: pending.id,
            status: pending.payment_status || pending.status,
            txn_ref: pending.txn_ref || '',
            method: pending.method || pending.batch,
          });
        }
      } catch {
        /* ignore */
      }
    };
    run();
  }, [token]);

  const handleDeclare = async (e) => {
    e.preventDefault();
    if (!txnRef.trim()) {
      toast.error('Please enter the transaction reference');
      return;
    }
    setDeclaring(true);
    try {
      const r = await axios.post(
        `${API}/enrollments/declare`,
        {
          level_number: 1,
          method: declareMethod,
          txn_ref: txnRef.trim(),
          screenshot_url: screenshotUrl.trim(),
          notes: declareNotes.trim(),
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Submitted for review · we will approve within 24h');
      setMyPending({
        id: r.data.enrollment_id,
        status: 'pending_review',
        txn_ref: txnRef.trim(),
        method: declareMethod,
      });
      setTxnRef('');
      setScreenshotUrl('');
      setDeclareNotes('');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not submit');
    } finally {
      setDeclaring(false);
    }
  };

  const handleRazorpayPay = async () => {
    setProcessing(true);
    try {
      const ok = await loadRazorpayScript();
      if (!ok) {
        toast.error(RAZORPAY_LOAD_ERROR);
        return;
      }

      const orderRes = await axios.post(
        `${API}/create-order`,
        { level_number: 1 },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const { order_id, amount, currency, key_id } = orderRes.data;
      const rzpKey = key_id || RAZORPAY_KEY_ID;
      if (!rzpKey) {
        toast.error('Payment gateway not configured');
        return;
      }

      const options = {
        key: rzpKey,
        amount,
        currency,
        name: 'Siddha Samaj',
        description: 'Grahita Śakti · Foundation Programme',
        order_id,
        // Intentionally no personal name/email prefill — the checkout should show
        // only the Siddha Samaj identity, not the signed-in user's personal name.
        theme: { color: '#d4a843' },
        handler: async (response) => {
          try {
            await axios.post(
              `${API}/verify-payment`,
              {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                level_number: 1,
              },
              { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success('Payment verified · You are enrolled!');
            await refreshUser();
            navigate('/courses/1');
          } catch (err) {
            toast.error(
              err?.response?.data?.detail || 'Payment verification failed'
            );
          }
        },
        modal: {
          ondismiss: () => toast.message('Payment cancelled'),
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', (resp) => {
        toast.error(resp?.error?.description || 'Payment failed');
      });
      rzp.open();
    } catch (err) {
      console.error(err);
      const serverMsg = err?.response?.data?.detail;
      toast.error(serverMsg || 'Payment could not be started. Please try again in a moment.');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="min-h-screen mystical-bg pt-28 pb-20 relative">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Header */}
          <div className="text-center mb-10">
            <OrnateDivider label="Enroll" variant="lotus" className="mb-6 max-w-xl mx-auto" />
            <h1
              className="text-4xl md:text-5xl font-heading font-bold golden-text text-glow tracking-wider mb-3"
              data-testid="enroll-title"
            >
              Grahita Shakti · Self-Paced
            </h1>
            <p className="text-primary/80 max-w-2xl mx-auto">
              A structured entry into the Siddha method. Live guided sessions,
              kriyas, handouts and a closed practice cohort.
            </p>
            <div className="mt-4 inline-flex items-center gap-3 px-4 py-2 rounded-full border border-primary/30 bg-primary/5">
              <span className="text-3xl font-heading golden-text">₹3,000</span>
              <span className="text-xs text-primary/60 uppercase tracking-[0.25em]">
                International payments accepted
              </span>
            </div>
          </div>

          {alreadyEnrolled && (
            <div
              className="card-mystical rounded-2xl p-6 mb-8 text-center"
              data-testid="already-enrolled-banner"
            >
              <CheckCircle2 className="text-primary mx-auto mb-2" />
              <p className="text-primary">You are already enrolled in Grahita Shakti.</p>
              <Button
                onClick={() => navigate('/courses/1')}
                className="ornate-button mt-4"
                data-testid="go-to-course-btn"
              >
                Go to course
              </Button>
            </div>
          )}

          {/* Tabs */}
          <div className="card-mystical rounded-2xl overflow-hidden">
            <div className="flex border-b border-primary/15">
              <TabButton
                active={tab === 'india'}
                onClick={() => setTab('india')}
                icon={MapPin}
                testid="tab-india"
              >
                India
              </TabButton>
              <TabButton
                active={tab === 'international'}
                onClick={() => setTab('international')}
                icon={Globe}
                testid="tab-international"
              >
                International
              </TabButton>
            </div>

            <div className="p-6 sm:p-8">
              {tab === 'india' ? (
                <div className="max-w-md mx-auto flex flex-col items-center text-center">
                  {/* Primary — UPI QR */}
                  <h3 className="text-xl font-heading text-primary mb-3 uppercase tracking-wider">
                    Pay with UPI
                  </h3>
                  <div className="rounded-xl border border-primary/25 p-3 bg-primary/5">
                    <img
                      src={UPI_QR}
                      alt="UPI QR Code"
                      className="w-[220px] h-[220px] rounded-md"
                      data-testid="upi-qr"
                    />
                  </div>
                  <p className="text-xs text-primary/60 mt-3 max-w-[260px]">
                    Scan with any UPI app (GPay, PhonePe, Paytm) to pay ₹3,000.
                  </p>
                  <div
                    className="mt-2 flex items-center gap-2 text-[11px] text-primary/55 tracking-wide"
                    data-testid="upi-id-caption"
                  >
                    <span>UPI&nbsp;ID:</span>
                    <code className="font-mono text-primary/80" data-testid="upi-id-value">
                      {UPI_ID}
                    </code>
                    <CopyButton value={UPI_ID} testid="copy-upi-btn" />
                  </div>

                  <p className="mt-5 text-[11px] text-primary/60 leading-relaxed max-w-[320px]">
                    After payment, submit your UPI reference in the form below —
                    you&apos;ll be enrolled within 24 hours.
                  </p>

                  {/* Divider */}
                  <div className="flex items-center gap-3 w-full max-w-xs my-7">
                    <div className="flex-1 h-px bg-primary/15" />
                    <span className="text-[10px] uppercase tracking-[0.3em] text-primary/50">or</span>
                    <div className="flex-1 h-px bg-primary/15" />
                  </div>

                  {/* Secondary — Razorpay */}
                  <h4 className="text-sm font-heading text-primary mb-3 uppercase tracking-[0.3em]">
                    Pay with Razorpay
                  </h4>
                  <Button
                    onClick={handleRazorpayPay}
                    disabled={processing || alreadyEnrolled}
                    className="ornate-button px-8 py-4 text-base"
                    data-testid="razorpay-pay-btn-india"
                  >
                    {processing ? 'Starting checkout…' : 'Pay ₹3,000 with Razorpay'}
                  </Button>
                  <div className="mt-3 flex items-center gap-2 text-[11px] text-primary/55">
                    <ShieldCheck size={12} /> UPI, cards &amp; net-banking · Instant enrollment
                  </div>
                </div>
              ) : (
                <div className="max-w-md mx-auto text-center">
                  <h3 className="text-xl font-heading text-primary mb-3 uppercase tracking-wider">
                    Pay with Card / Net-banking
                  </h3>
                  <p className="text-sm text-primary/70 mb-5">
                    Secure <strong>Razorpay</strong> checkout — international cards,
                    UPI and net-banking accepted. You will be enrolled automatically
                    on successful payment.
                  </p>
                  <Button
                    onClick={handleRazorpayPay}
                    disabled={processing || alreadyEnrolled}
                    className="ornate-button px-8 py-4 text-base"
                    data-testid="razorpay-pay-btn"
                  >
                    {processing ? 'Starting checkout…' : 'Pay ₹3,000 with Razorpay'}
                  </Button>
                  <div className="mt-4 flex items-center justify-center gap-2 text-xs text-primary/60">
                    <ShieldCheck size={14} /> Encrypted · PCI-DSS compliant
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="text-center mt-8">
            <button
              type="button"
              onClick={() => navigate('/courses/1')}
              className="text-xs text-primary/60 hover:text-primary uppercase tracking-[0.3em]"
              data-testid="back-to-course"
            >
              ← Back to course
            </button>
          </div>

          {/* Self-declare payment (UPI / Bank / Remitly) */}
          {!alreadyEnrolled && (
            <div className="mt-12">
              <OrnateDivider
                label="Already paid?"
                variant="diamond"
                className="mb-6 max-w-xl mx-auto"
              />
              {myPending ? (
                <div
                  className="card-mystical rounded-2xl p-6 text-center"
                  data-testid="pending-review-card"
                >
                  <Clock className="text-primary mx-auto mb-2" />
                  <p className="text-primary font-heading uppercase tracking-wider mb-1">
                    Submitted — Under review
                  </p>
                  <p className="text-sm text-primary/70">
                    Method: <span className="uppercase">{myPending.method}</span> ·
                    Ref: <code className="font-mono">{myPending.txn_ref}</code>
                  </p>
                  <p className="text-xs text-primary/50 mt-2">
                    We usually verify within 24 hours. You will be enrolled automatically once approved.
                  </p>
                </div>
              ) : (
                <form
                  onSubmit={handleDeclare}
                  className="card-mystical rounded-2xl p-6 space-y-4"
                  data-testid="declare-form"
                >
                  <div>
                    <p className="text-sm text-primary/80 mb-2">
                      Paid via UPI, bank transfer or Remitly? Submit the transaction
                      reference below and we will verify and enroll you.
                    </p>
                  </div>

                  <div>
                    <label className="block text-xs uppercase tracking-[0.3em] text-primary/60 mb-2">
                      Payment method
                    </label>
                    <div className="flex gap-2 flex-wrap">
                      {['upi', 'bank', 'other'].map((m) => (
                        <button
                          key={m}
                          type="button"
                          onClick={() => setDeclareMethod(m)}
                          data-testid={`declare-method-${m}`}
                          className={`px-4 py-2 text-sm rounded-full border transition ${
                            declareMethod === m
                              ? 'border-primary bg-primary/10 text-primary'
                              : 'border-primary/30 text-primary/60 hover:border-primary/60'
                          }`}
                        >
                          {m === 'upi'
                            ? 'UPI'
                            : m === 'bank'
                            ? 'Bank transfer'
                            : 'Other (Remitly / Wise)'}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label
                      htmlFor="txn-ref"
                      className="block text-xs uppercase tracking-[0.3em] text-primary/60 mb-2"
                    >
                      Transaction reference / UTR / UPI ref
                    </label>
                    <input
                      id="txn-ref"
                      type="text"
                      value={txnRef}
                      onChange={(e) => setTxnRef(e.target.value)}
                      placeholder="e.g. 419823648127"
                      className="w-full bg-primary/5 border border-primary/25 rounded-lg px-3 py-2 text-primary focus:outline-none focus:border-primary/60"
                      data-testid="declare-txn-input"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="screenshot-url"
                      className="block text-xs uppercase tracking-[0.3em] text-primary/60 mb-2"
                    >
                      Screenshot URL (optional)
                    </label>
                    <input
                      id="screenshot-url"
                      type="url"
                      value={screenshotUrl}
                      onChange={(e) => setScreenshotUrl(e.target.value)}
                      placeholder="https://… link to your payment screenshot"
                      className="w-full bg-primary/5 border border-primary/25 rounded-lg px-3 py-2 text-primary focus:outline-none focus:border-primary/60"
                      data-testid="declare-screenshot-input"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="declare-notes"
                      className="block text-xs uppercase tracking-[0.3em] text-primary/60 mb-2"
                    >
                      Notes (optional)
                    </label>
                    <textarea
                      id="declare-notes"
                      value={declareNotes}
                      onChange={(e) => setDeclareNotes(e.target.value)}
                      rows={2}
                      placeholder="Any context for the admin"
                      className="w-full bg-primary/5 border border-primary/25 rounded-lg px-3 py-2 text-primary focus:outline-none focus:border-primary/60"
                      data-testid="declare-notes-input"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={declaring}
                    className="ornate-button w-full"
                    data-testid="declare-submit-btn"
                  >
                    <Upload size={14} />{' '}
                    {declaring ? 'Submitting…' : 'Submit payment for review'}
                  </Button>
                </form>
              )}
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default EnrollLevel1;
